import { CANCEL } from '../../../config/constants.js';

/**
 * Bullet cancel (BULLET-HELL.md §D2) — the conversion itself, kept free of the
 * game so it can be tested against a bare pool.
 *
 * Deliberately a pure-ish function rather than a system: the two callers (a
 * boss phase break and an ultimate activation, both wired in Game) already
 * know which pools are live and what to do with the drops, and everything
 * interesting here — pricing a bullet, capping the payout — is arithmetic that
 * deserves a test more than it deserves an object.
 *
 * Note the cap semantics: EVERY bullet is always cancelled (the readability
 * release valve is the half of this mechanic that must never be rationed), but
 * at most `cap` motes are produced. The excess XP is not lost, it is pooled
 * across the motes that do drop, so the payout curve stays honest past the cap
 * while the floor stays walkable.
 */

/** XP a single bullet is worth when cancelled — scaled, sublinearly, by size. */
export function bulletCancelXp(bullet) {
  const scale = Math.min(
    CANCEL.radiusScaleMax,
    Math.max(CANCEL.radiusScaleMin, Math.sqrt((bullet.radius || CANCEL.referenceRadius) / CANCEL.referenceRadius)),
  );
  return CANCEL.xpPerBullet * scale;
}

/** Which MOTES.tiers entry a cancelled bullet drops as — size/colour only. */
export function bulletCancelTier(bullet) {
  const r = bullet.radius || 0;
  if (r >= CANCEL.largeRadius) return 'large';
  if (r >= CANCEL.mediumRadius) return 'medium';
  return 'small';
}

/**
 * Deactivates every live bullet across `pools` and returns the motes they
 * convert into: `{ drops: [{ x, y, xp, tier }], cancelled, xp }`.
 *
 * `pools` takes anything with a `forEachBullet(fn)` — a SpawningSystem, a
 * second boss's SpawningSystem, MinionWaveSystem — and skips nulls, so callers
 * can pass the run's whole roster without checking which halves exist.
 */
export function cancelBullets(pools, { cap = CANCEL.maxMotes, xpMultiplier = 1 } = {}) {
  const hits = [];
  let xp = 0;
  for (const pool of pools) {
    if (!pool || typeof pool.forEachBullet !== 'function') continue;
    pool.forEachBullet((bullet) => {
      if (!bullet.active) return;
      bullet.active = false;
      const value = bulletCancelXp(bullet) * xpMultiplier;
      xp += value;
      hits.push({ x: bullet.x, y: bullet.y, xp: value, tier: bulletCancelTier(bullet) });
    });
  }
  const cancelled = hits.length;
  if (!cancelled || cap <= 0) return { drops: [], cancelled, xp };
  if (cancelled <= cap) return { drops: hits, cancelled, xp };

  // Over the cap: keep an evenly-strided subset rather than the first `cap`,
  // so what survives is spread across the whole curtain instead of clustered
  // wherever the first pattern happened to be firing. Each survivor carries an
  // equal share of the total, which is also what makes the cap invisible in
  // the payout — only in the number of things to walk over.
  const drops = [];
  const share = xp / cap;
  for (let i = 0; i < cap; i++) {
    const hit = hits[Math.floor((i * cancelled) / cap)];
    drops.push({ x: hit.x, y: hit.y, xp: share, tier: hit.tier });
  }
  return { drops, cancelled, xp };
}
